<script setup>
import { storeToRefs } from 'pinia'
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useMainStore } from '@/stores/useMainStore.js'
import { useRechnerStore } from '@/stores/rechner.js'
import { useTarifinfosStore } from '@/stores/tarifinfos.js'


import fallback from '@/components/general/fallback.vue'

import PflegeView from '@/views/PflegeView.vue'

import arn_schritt0 from '@/views/arn/arn_schritt0.vue'
import arn_schritt1 from '@/views/arn/arn_schritt1.vue'
import arn_schritt2 from '@/views/arn/arn_schritt2.vue'
import arn_schritt3 from '@/views/arn/arn_schritt3.vue'
import arn_schritt4 from '@/views/arn/arn_schritt4.vue'
import arn_schritt5 from '@/views/arn/arn_schritt5.vue'

import ar20_schritt0 from '@/views/ar20/ar20_schritt0.vue'
import ar20_schritt1 from '@/views/ar20/ar20_schritt1.vue'
import ar20_schritt2 from '@/views/ar20/ar20_schritt2.vue'
import ar20_schritt3 from '@/views/ar20/ar20_schritt3.vue'
import ar20_schritt4 from '@/views/ar20/ar20_schritt4.vue'
import ar20_schritt5 from '@/views/ar20/ar20_schritt5.vue'


import Loader from '@/components/loader.vue'
import ApiErrorPage from '@/components/ApiErrorPage.vue'
import bestaetigung from '@/views/bestaetigen/bestaetigung.vue'
import bestaetigung_legacy from '@/views/bestaetigen/legacy.vue'

import versioninfo from '@/components/versioninfo.vue'
import devStep from '@/components/devStep.vue'

import kombi_schritt0 from '@/views/kombi/kombi_schritt0.vue'
import kombi_schritt1 from '@/views/kombi/kombi_schritt1.vue'
import kombi_schritt2 from '@/views/kombi/kombi_schritt2.vue'
import kombi_schritt3 from '@/views/kombi/kombi_schritt3.vue'
import kombi_schritt4 from '@/views/kombi/kombi_schritt4.vue'
import kombi_schritt5 from '@/views/kombi/kombi_schritt5.vue'

const store = useMainStore()
const rechnerStore = useRechnerStore()
const tarifinfosStore = useTarifinfosStore()
const { dataAttributes } = storeToRefs(store)
const funktion = computed(() => dataAttributes.value.funktion)

// Reactive states für Initialisierung
const isInitializing = ref(true)
const hasLoadError = ref(false)

// Ref für Widget-Root zum Scrollen
const widgetRoot = ref(null)

// Dynamische Komponenten-Auswahl basierend auf data-rechner
const currentComponent = computed(() => {
  const rechnerType = dataAttributes.value.rechner
  const step = rechnerStore.stepStatus
  
  // Wenn funktion === '2', zeige Bestätigungsseite (macht API-Call und zeigt Erfolg/Fehler)
  if (funktion.value === '2') {
    return bestaetigung
  }

   // Wenn funktion 4,5,6, zeige Legacy-Bestätigungsseite (macht API-Call und zeigt Erfolg/Fehler)
  if (['4', '5', '6'].includes(funktion.value)) {
    return bestaetigung_legacy
  }
  
  // Rest der bestehenden Logik (nur bei funktion === '1')

  // Kombi-Rechner mit Steps
  if (rechnerType === 'kombi') {
    switch (step) {
      case 0: return kombi_schritt0           
      case 1: return kombi_schritt1 
      case 2: return kombi_schritt2 
      case 3: return kombi_schritt3 
      case 4: return kombi_schritt4 
      case 5: return kombi_schritt5
      default: return fallback
    }
  }

  if (rechnerType === 'kombistat') {
    switch (step) {
      case 0: return kombi_schritt0           
      case 1: return kombi_schritt1 
      case 2: return kombi_schritt2 
      case 3: return kombi_schritt3 
      case 4: return kombi_schritt4 
      case 5: return kombi_schritt5      
      default: return fallback
    }
  }
  
  // ARN-Rechner mit Steps
  if (rechnerType === 'arn') {        
    switch (step) {
      case 0: return arn_schritt0
      case 1: return arn_schritt1
      case 2: return arn_schritt2
      case 3: return arn_schritt3
      case 4: return arn_schritt4
      case 5: return arn_schritt5
      default: return fallback
    }
  }
  
  // AR20-Rechner mit Steps
  if (rechnerType === 'ar20') {
    switch (step) {
      case 0: return ar20_schritt0
      case 1: return ar20_schritt1
      case 2: return ar20_schritt2
      case 3: return ar20_schritt3
      case 4: return ar20_schritt4
      case 5: return ar20_schritt5
      default: return fallback
    }
  }
  
  // Pflege (ohne Steps)
  if (rechnerType === 'pflege') {
    return PflegeView
  }
  
  // Fallback
  
  return fallback
  
})

// Lädt Tarifinfos beim Start
async function loadInitialData() {
  try {
    isInitializing.value = true
    hasLoadError.value = false
    
    // Tarifinfos laden
    const success = await tarifinfosStore.loadTarifinfos()
    //const success = true
    
    if (success) {
      // Nach erfolgreichem Laden: Preise mit initialerPreis aktualisieren
      rechnerStore.calculateAll()
    } else {
      hasLoadError.value = true
    }
  } catch (error) {
    console.error('Fehler beim Laden der Tarifinfos:', error)
    hasLoadError.value = true
  } finally {
    isInitializing.value = false
  }
}

// Retry-Funktion für Fehlerseite
function retryLoading() {
  loadInitialData()
}

// Store einmalig beim App-Start initialisieren
onMounted(async () => {
  rechnerStore.initializeStore()
  
  // API nur laden wenn funktion === '1'
  if (funktion.value === '1') {
    await loadInitialData()
  } else {
    // Bei funktion === '2' sofort als geladen markieren
    isInitializing.value = false
  }
})

// Watch für Schrittwechsel - scrollt zum Widget-Anfang
watch(() => rechnerStore.stepStatus, async (newStep, oldStep) => {
  // Nur scrollen wenn sich der Step wirklich ändert und nicht beim initialen Laden
  if (oldStep !== undefined && newStep !== oldStep && widgetRoot.value) {
    // Warten bis DOM aktualisiert ist
    await nextTick()
    
    // Position des Widget-Root ermitteln
    const rect = widgetRoot.value.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    
    // Mit zusätzlichem Offset nach oben scrollen (z.B. 100px für transform-verschobene Elemente)
    const targetPosition = rect.top + scrollTop - 100
    
    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: 'smooth'
    })
  }
})
</script>

<template>
  <div class="vigotr" ref="widgetRoot">
    <!-- Loader während Initialisierung -->
    <Loader v-if="isInitializing" />
    
    <!-- Fehlerseite bei Ladefehler -->
    <ApiErrorPage 
      v-else-if="hasLoadError"
      :statusCode="tarifinfosStore.httpStatusCode"
      :errorMessage="tarifinfosStore.error"
      @retry="retryLoading"
    />
    
    <!-- Normale App nach erfolgreichem Laden -->
    <component v-else :is="currentComponent" />    
    <versioninfo />
   
  </div>
</template>

<style scoped>
</style>
